import React, { useState } from 'react'
import Table, { TableOwnProps } from '@mui/material/Table'
import { InvestorDialog } from './AdminInvestorDialog'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import TableBody from '@mui/material/TableBody'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import { InvestorFormWithId, validateFn } from './AdminInvestorsFields'
import { formatMoney, formatPercentage } from '../../../../packages/utils/numberFormatters'
import { InvestorsFieldsSanityCheck, InvestorsFieldsSanityCheckProps } from './AdminInvestorFieldsSanityCheck'

export function InvestorsTable({
    currency,
    investors,
    handleDelete,
    handleSubmit,
    isFeeLevelEnabled,
    isLeoEnabled,
    isWithoutRightsEnabled,
    isPercentageEnabled,
    isSellerEnabled,
    isPutOptionEnabled,
    sanityChecker,
    validateName,
    validateAmount,
    validateFeeLevel,
    validateLeo,
    ...props
}: {
    currency: string | null
    investors: InvestorFormWithId[]
    handleDelete: (inv: InvestorFormWithId) => void
    handleSubmit: (inv: InvestorFormWithId) => void
    isFeeLevelEnabled: boolean
    isLeoEnabled: boolean
    isWithoutRightsEnabled: boolean
    isPercentageEnabled: boolean
    isSellerEnabled: boolean
    isPutOptionEnabled: boolean
    sanityChecker: (investors: InvestorFormWithId[]) => InvestorsFieldsSanityCheckProps
    validateName: validateFn
    validateAmount: validateFn
    validateFeeLevel: validateFn
    validateLeo: validateFn
} & TableOwnProps) {
    const hasPercentage = investors.some((p) => p.amountInPercentage)
    const hasCash = investors.some((p) => p.amountInCash)
    const [investorToEdit, setInvestorToEdit] = useState<InvestorFormWithId | null>(null)
    const [investorFormOpen, setInvestorFormOpen] = useState(false)
    const locale = 'sv'

    const editFormOpen = (investor: InvestorFormWithId) => {
        setInvestorToEdit(investor)
        setInvestorFormOpen(true)
    }
    const newFormOpen = () => {
        setInvestorToEdit(null)
        setInvestorFormOpen(true)
    }

    const { totals } = sanityChecker(investors)

    return (
        <>
            {investorFormOpen && (
                <InvestorDialog
                    open={investorFormOpen}
                    setOpen={setInvestorFormOpen}
                    handleSubmitInvestor={handleSubmit}
                    investor={investorToEdit}
                    isFeeLevelEnabled={isFeeLevelEnabled}
                    isLeoEnabled={isLeoEnabled}
                    isWithoutRightsEnabled={isWithoutRightsEnabled}
                    isPercentageEnabled={isPercentageEnabled}
                    isSellerEnabled={isSellerEnabled}
                    isPutOptionEnabled={isPutOptionEnabled}
                    validateName={validateName}
                    validateAmount={validateAmount}
                    validateFeeLevel={validateFeeLevel}
                    validateLeo={validateLeo}
                />
            )}
            <TableContainer>
                <Table size='small' {...props}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Namn</TableCell>
                            {hasPercentage && <TableCell>Procent</TableCell>}
                            {hasCash && <TableCell>Belopp</TableCell>}
                            {(isLeoEnabled || isFeeLevelEnabled) && <TableCell>Nivå/Leo</TableCell>}
                            {isSellerEnabled && <TableCell>Säljare av befintliga aktier</TableCell>}
                            {isPutOptionEnabled && <TableCell>Säljoptionsavtal</TableCell>}
                            <TableCell></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {investors.map((investor) => {
                            return (
                                <TableRow key={investor.tempId || investor.id}>
                                    <TableCell>{investor.name}</TableCell>
                                    {hasPercentage && <TableCell>{investor.amountInPercentage ?? '-'}</TableCell>}
                                    {hasCash && (
                                        <TableCell>
                                            {formatMoney(investor.amountInCash, {
                                                locale,
                                                decimals: 0,
                                                currencySymbol: currency,
                                            }) || '-'}
                                        </TableCell>
                                    )}
                                    {(isLeoEnabled || isFeeLevelEnabled) && (
                                        <TableCell>
                                            {!!investor.leo && <Typography>Leo</Typography>}
                                            {!investor.leo &&
                                                (isWithoutRightsEnabled &&
                                                investor.feeLevel !== null &&
                                                +investor.feeLevel === 0 &&
                                                investor.withoutRights ? (
                                                    <Typography>TÅ utan rätter</Typography>
                                                ) : investor.feeLevel === null ? (
                                                    '-'
                                                ) : (
                                                    investor.feeLevel
                                                ))}
                                        </TableCell>
                                    )}
                                    {isSellerEnabled && <TableCell>{!!investor.isSeller && 'Ja'}</TableCell>}
                                    {isPutOptionEnabled && <TableCell>{!!investor.putOption && 'Ja'}</TableCell>}
                                    <TableCell>
                                        <Button size='small' onClick={() => editFormOpen(investor)}>
                                            Ändra
                                        </Button>
                                        <Button size='small' onClick={() => handleDelete(investor)}>
                                            [Ta bort]
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            )
                        })}
                        <TableRow>
                            <TableCell>
                                <Typography variant='body2'>Summa</Typography>
                            </TableCell>
                            <TableCell colSpan={4}>
                                <Typography variant='body2'>
                                    {formatMoney(totals.cash, { locale, decimals: 0, currencySymbol: currency })} (
                                    {formatPercentage(totals.percentage, { locale, decimals: 1 })})
                                </Typography>
                                <InvestorsFieldsSanityCheck investors={investors} sanityChecker={sanityChecker} />
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell colSpan={4}>
                                <Button onClick={newFormOpen}>Ny investerare</Button>
                            </TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    )
}
